import { cn } from '@/lib/utils';

// ─── Types ──────────────────────────────────────────────────────────────────

export type TabId = 'overview' | 'pipeline-activity' | 'needs-review' | 'portfolio-risk' | 'team-monitoring';

interface Tab {
  id: TabId;
  label: string;
  badge?: number | null;
  badgeVariant?: 'default' | 'warning' | 'error';
  adminOnly?: boolean;
}

interface DashboardTabsProps {
  activeTab: TabId;
  onTabChange: (id: TabId) => void;
  reviewQueueCount?: number | null;
  reviewQueueError?: boolean;
  portfolioRiskCount?: number | null;
  itemsWaitingOnMe?: number | null;
  isAdmin?: boolean;
}

// ─── Badge component ─────────────────────────────────────────────────────────

function TabBadge({
  count,
  variant,
  error,
}: {
  count: number | null | undefined;
  variant?: string;
  error?: boolean;
}) {
  if (error) {
    return (
      <span className="ml-2 inline-flex items-center rounded-full bg-red-100 px-1.5 py-0.5 text-xs font-bold text-red-700 dark:bg-red-900 dark:text-red-300">
        !
      </span>
    );
  }
  // Number.isFinite, not just a null/zero check: NaN passes both `== null` and
  // `=== 0`, so a count that arrived as NaN would render a badge reading "NaN"
  // next to the tab label. No count is better than a wrong one.
  if (count == null || !Number.isFinite(count) || count === 0) return null;
  const colorClass =
    variant === 'warning'
      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
      : 'bg-muted text-muted-foreground';
  return (
    <span className={cn('ml-2 inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-bold', colorClass)}>
      {count}
    </span>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * WCAG-compliant tab navigation for the dashboard.
 *
 * Implements:
 * - role="tablist" on the container
 * - role="tab" + aria-selected + aria-controls on each tab
 * - Keyboard: Left/Right Arrow to move between tabs, Enter/Space to activate
 *
 * Tabs:
 *   1. Overview (all roles)
 *   2. Needs Review (all roles) — count badge
 *   3. Portfolio Risk (all roles) — FR-086
 *   4. Team Monitoring (admin only) — FR-083/084
 */
export function DashboardTabs({
  activeTab,
  onTabChange,
  reviewQueueCount,
  reviewQueueError = false,
  portfolioRiskCount,
  itemsWaitingOnMe,
  isAdmin = false,
}: DashboardTabsProps) {
  const allTabs: Tab[] = [
    { id: 'overview', label: 'Overview' },
    {
      id: 'pipeline-activity',
      label: 'Pipeline Activity',
      badge: itemsWaitingOnMe,
      badgeVariant: itemsWaitingOnMe && itemsWaitingOnMe > 0 ? 'warning' : 'default',
    },
    {
      id: 'needs-review',
      label: 'Needs Review',
      badge: reviewQueueCount,
      badgeVariant: reviewQueueCount && reviewQueueCount > 0 ? 'warning' : 'default',
    },
    {
      id: 'portfolio-risk',
      label: 'Portfolio Risk',
      badge: portfolioRiskCount,
    },
    {
      id: 'team-monitoring',
      label: 'Team Monitoring',
      adminOnly: true,
    },
  ];

  // Filter out admin-only tabs for non-admins
  const tabs = allTabs.filter((t) => !t.adminOnly || isAdmin);

  function handleKeyDown(e: React.KeyboardEvent, index: number) {
    if (e.key === 'ArrowRight') {
      const next = (index + 1) % tabs.length;
      onTabChange(tabs[next].id);
    } else if (e.key === 'ArrowLeft') {
      const prev = (index - 1 + tabs.length) % tabs.length;
      onTabChange(tabs[prev].id);
    }
  }

  return (
    <div
      role="tablist"
      aria-label="Dashboard sections"
      className="flex gap-1 border-b border-border"
    >
      {tabs.map((tab, index) => {
        const isActive = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={isActive}
            aria-controls={`tabpanel-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => { onTabChange(tab.id); }}
            onKeyDown={(e) => { handleKeyDown(e, index); }}
            className={cn(
              'inline-flex items-center px-4 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-t',
              isActive
                ? 'border-b-2 border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/40',
            )}
          >
            {tab.label}
            {tab.id === 'needs-review' ? (
              <TabBadge count={reviewQueueCount} variant={tab.badgeVariant} error={reviewQueueError} />
            ) : (
              <TabBadge count={tab.badge} />
            )}
          </button>
        );
      })}
    </div>
  );
}
